/**
 * Hero depth-scroll (layered parallax).
 * Background media lags behind the scroll while the content lifts + fades,
 * separating foreground from background to create depth.
 *
 * - Driven by requestAnimationFrame on a passive scroll listener (works everywhere,
 *   unlike background-attachment:fixed or CSS scroll-timelines).
 * - Gentler on mobile; fully disabled under prefers-reduced-motion.
 * - Only runs while the hero is on screen (IntersectionObserver gate).
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobile = window.matchMedia('(max-width: 749px)');

  // Cached so the media query isn't re-evaluated on every animation frame.
  var cfg = null;
  function settings() {
    // Scale values are mirrored in section-hero-banner.css
    // (html.js [data-hero-parallax] .hero__media) so first paint matches the
    // settled state (BBQ-45). Keep both in sync when tuning.
    if (!cfg) {
      cfg = mobile.matches
        ? { scale: 1.18, lift: 40, fade: 1.1 } // gentler on phones
        : { scale: 1.34, lift: 110, fade: 1.15 }; // strong on desktop
    }
    return cfg;
  }
  function invalidateSettings() { cfg = null; }

  // Centred progress: -1..1, 0 when the hero is centred in the viewport.
  function progress(top, h, vh) {
    var p = (vh / 2 - (top + h / 2)) / (vh / 2 + h / 2);
    return p < -1 ? -1 : p > 1 ? 1 : p;
  }

  function init(inner) {
    // Clean up a previous instance on this element (theme editor re-render).
    if (typeof inner.__heroParallaxCleanup === 'function') inner.__heroParallaxCleanup();

    var allMedia = inner.querySelectorAll('.hero__media');
    var content = inner.querySelector('.hero__content');
    if (!allMedia.length && !content) return;

    // BBQ-48: only the breakpoint's visible media is animated. The other one is
    // display:none, so transform writes and a promoted layer are pure waste.
    // Recomputed on resize/breakpoint change, never per frame.
    var media = [];
    function refreshMedia() {
      media.forEach(function (m) { m.style.willChange = ''; m.style.transform = ''; });
      media = Array.prototype.filter.call(allMedia, function (m) {
        return m.getClientRects().length > 0;
      });
      // Nothing animates under reduced motion, so don't hold compositor layers.
      if (reduceMotion.matches) return;
      media.forEach(function (m) { m.style.willChange = 'transform'; });
      if (content) content.style.willChange = 'transform, opacity';
    }

    var ticking = false;
    var onScreen = false;
    var scrollBound = false;
    var observer = null;

    function reset() {
      media.forEach(function (m) { m.style.transform = ''; m.style.willChange = ''; });
      if (content) {
        content.style.transform = '';
        content.style.opacity = '';
        content.style.willChange = '';
      }
    }

    function frame() {
      ticking = false;
      if (!onScreen || reduceMotion.matches) return;

      var cfg = settings();
      var rect = inner.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var h = rect.height || 1;

      var p = progress(rect.top, h, vh);

      // BBQ-48: measure progress relative to the hero's resting position rather
      // than absolute viewport centring, so the translate is exactly 0 at
      // scrollY 0. BBQ-45 pre-applied the scale in CSS but not the translate,
      // which left the media snapping ~6px the moment this script booted.
      // rect.top + scrollY is the constant document offset, so no extra layout
      // read and it stays correct across resize.
      // Normalising by (1 + |rest|) instead of clamping keeps |d| <= 1 (so the
      // scaled edges can never reveal) without saturating: for a hero far down
      // the page rest clamps to -1, and a hard clamp on d would freeze the
      // parallax at +maxShift for the second half of the pass.
      var rest = progress(rect.top + window.scrollY, h, vh);
      var d = (p - rest) / (1 + Math.abs(rest));

      // Background lags within the scale slack so edges never reveal.
      var maxShift = ((cfg.scale - 1) * h) / 2;
      var y = (d * maxShift).toFixed(2);
      var t = 'translate3d(0,' + y + 'px,0) scale(' + cfg.scale + ')';
      media.forEach(function (m) { m.style.transform = t; });

      // Content lifts + fades as the hero scrolls away.
      if (content) {
        var scrolled = -rect.top;
        if (scrolled < 0) scrolled = 0; else if (scrolled > h) scrolled = h;
        var frac = scrolled / h;
        content.style.transform = 'translate3d(0,' + (-cfg.lift * frac).toFixed(2) + 'px,0)';
        var op = 1 - frac * cfg.fade;
        content.style.opacity = String(op < 0 ? 0 : op);
      }
    }

    function request() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(frame);
      }
    }

    // BBQ-48: refreshMedia() also promotes .hero__content, whose transform and
    // opacity are rewritten every frame; without that it was never promoted,
    // so each frame repainted the hero heading and buttons instead of
    // compositing them.
    refreshMedia();

    // BBQ-48: bind/unbind the scroll listener with visibility instead of
    // keeping it for the life of the page. Previously every scroll event on
    // every part of the page still scheduled a rAF that immediately bailed.
    function bindScroll() {
      if (scrollBound) return;
      scrollBound = true;
      window.addEventListener('scroll', request, { passive: true });
    }
    function unbindScroll() {
      if (!scrollBound) return;
      scrollBound = false;
      window.removeEventListener('scroll', request);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        onScreen = entries[entries.length - 1].isIntersecting;
        if (onScreen) { bindScroll(); request(); } else { unbindScroll(); }
      });
      observer.observe(inner);
    } else {
      onScreen = true;
      bindScroll();
    }

    // invalidateSettings() here too, not just on the mql change event: on
    // browsers where MediaQueryList lacks addEventListener the breakpoint
    // listener never fires, and the cached cfg would keep the wrong
    // scale/lift after a rotation. One matchMedia re-read per resize is free.
    function onResize() { invalidateSettings(); refreshMedia(); request(); }
    window.addEventListener('resize', onResize, { passive: true });
    function onBreakpointChange() { invalidateSettings(); refreshMedia(); request(); }
    if (mobile.addEventListener) mobile.addEventListener('change', onBreakpointChange);

    function onMotionChange() {
      if (reduceMotion.matches) reset(); else { refreshMedia(); request(); }
    }
    if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onMotionChange);

    if (reduceMotion.matches) reset(); else request();

    // Expose a cleanup so re-init / unload removes listeners + observer (no leak).
    inner.__heroParallaxCleanup = function () {
      unbindScroll();
      window.removeEventListener('resize', onResize);
      if (mobile.removeEventListener) mobile.removeEventListener('change', onBreakpointChange);
      if (reduceMotion.removeEventListener) reduceMotion.removeEventListener('change', onMotionChange);
      if (observer) observer.disconnect();
      media.forEach(function (m) { m.style.willChange = ''; });
      reset();
      inner.__heroParallaxCleanup = null;
    };
  }

  function boot() {
    var heroes = document.querySelectorAll('[data-hero-parallax]');
    for (var i = 0; i < heroes.length; i++) init(heroes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the section is re-rendered in the theme editor.
  // init() cleans up any previous instance on the element first, so listeners
  // and observers are never accumulated across re-renders.
  document.addEventListener('shopify:section:load', function (e) {
    var heroes = e.target.querySelectorAll('[data-hero-parallax]');
    for (var i = 0; i < heroes.length; i++) init(heroes[i]);
  });

  // Tear down when the section is removed in the theme editor.
  document.addEventListener('shopify:section:unload', function (e) {
    var heroes = e.target.querySelectorAll('[data-hero-parallax]');
    for (var i = 0; i < heroes.length; i++) {
      if (typeof heroes[i].__heroParallaxCleanup === 'function') heroes[i].__heroParallaxCleanup();
    }
  });
})();
